import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { useModules, type ModuleDetail, type RoleBindingStatus } from './use-modules.js';

export function ModulesListPage(): JSX.Element {
  const { companyId } = useParams<{ companyId: string }>();
  const { data, isLoading, error } = useModules(companyId);

  if (isLoading) return <p className="text-slate-500">Loading modules…</p>;
  if (error) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-800">
        <p className="font-medium">Could not load modules.</p>
        <p className="text-sm">{error.message}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">HR automation modules</h1>
          <p className="mt-1 text-sm text-slate-600">
            Each module is a self-contained HR automation. Configure and enable the modules your
            company uses; disabled modules reject new runs.
          </p>
        </div>
        <Link to="/" className="text-sm text-brand-600 hover:text-brand-700">
          ← Back
        </Link>
      </div>

      <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {(data?.items ?? []).map((m) => (
          <li key={m.id}>
            <Link
              to={`/companies/${companyId}/modules/${m.id}`}
              className="block rounded-lg border border-slate-200 bg-white p-4 transition hover:border-brand-400 hover:shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-semibold text-slate-900">{m.title}</h2>
                  <p className="mt-0.5 text-xs text-slate-500">
                    v{m.registryVersion}
                    {m.storedVersion !== null && m.storedVersion !== m.registryVersion
                      ? ` (stored v${m.storedVersion})`
                      : ''}
                  </p>
                </div>
                <EnabledBadge m={m} />
              </div>
              <p className="mt-2 text-xs text-slate-600">{m.description}</p>

              <div className="mt-3 space-y-1 text-xs">
                <p className="font-medium text-slate-700">Role bindings:</p>
                <ul className="flex flex-wrap gap-1">
                  {m.roleBindings.map((b) => (
                    <li key={b.role}>
                      <RoleChip b={b} />
                    </li>
                  ))}
                </ul>
              </div>

              {m.upgradeAvailable && (
                <p className="mt-3 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  Upgrade available
                </p>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RoleChip({ b }: { b: RoleBindingStatus }): JSX.Element {
  const status = b.connectionStatus;
  const good = status === 'connected';
  const missing = status === null;
  return (
    <span
      className={clsx(
        'inline-flex rounded-full px-2 py-0.5 font-medium',
        good
          ? 'bg-green-100 text-green-800'
          : missing && !b.optional
            ? 'bg-red-100 text-red-800'
            : 'bg-slate-100 text-slate-700',
      )}
      title={b.description}
    >
      {b.role}: {b.provider}/{b.connectionName}
      {good ? ' ✓' : missing ? (b.optional ? ' (optional)' : ' (missing)') : ` (${status})`}
    </span>
  );
}

function EnabledBadge({ m }: { m: ModuleDetail }): JSX.Element {
  if (m.enabled) {
    return (
      <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        enabled
      </span>
    );
  }
  if (m.storedVersion === null) {
    return (
      <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
        not configured
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
      disabled
    </span>
  );
}
